import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  CircleHelp,
  Pencil,
  RefreshCw,
} from "lucide-react";
import { patch } from "../api";
import type { Row } from "../api";
import { canCapture } from "../capture";
import {
  friendlyFieldLabel,
  friendlyValue,
  hasTechnicalDependency,
  schemaForQuestion,
  validateIntakeValue,
} from "../intake-values";
import { Button, Callout, Empty } from "../ui";
import { IntakeValueFields } from "./IntakeFields";
import { validateTaxFields } from "../field-validation";
import { focusValidationIssue } from "./ValidationSummary";
import { normalizeTaxAge } from "../tax-field-values";
import "./intake.css";

export interface IntakeExperienceProps {
  kind: string;
  catalog: Row;
  selected: Row | null;
  role: string;
  refresh: () => Promise<void>;
  notify: (message: string) => void;
  onTechnicalDetails?: (record: Row) => void;
}
export interface IntakeCard {
  record: Row;
  captureKind: string;
  title: string;
  help: string;
  section: string;
  schema: Row;
  available: boolean;
}
export interface IntakeRequest {
  caseId: string;
  fieldId: string;
  container: "answers" | "facts";
  url: string;
  body: Row;
}

const questionCopy: Record<string, [string, string]> = {
  "Q-001": [
    "Tax year",
    "Which tax year are you preparing? Choose explicitly; an unanswered question is not a confirmation.",
  ],
  "Q-002": [
    "Preparation dates",
    "Record the preparation date and when the information and documents were last collected.",
  ],
  "Q-003": [
    "Return history",
    "Confirm whether this is an original, unfiled calendar-year return and check for earlier filings.",
  ],
  "Q-004": [
    "Special filing circumstances",
    "Check for a deceased taxpayer, bankruptcy, disaster relief, military relief or another special circumstance.",
  ],
  "Q-005": [
    "Requested service",
    "Confirm what the taxpayer is asking you to do. This workspace does not file returns or move money.",
  ],
  "Q-006": [
    "Taxpayer identity",
    "Compare the taxpayer’s identity with the supporting documents. Use synthetic information in this workspace.",
  ],
  "Q-007": [
    "Citizenship and residence",
    "Record citizenship and where the taxpayer lived during the tax year.",
  ],
  "Q-008": [
    "Filing status",
    "Check marital status at year end and circumstances that could affect filing status.",
  ],
  "Q-009": [
    "Dependents and qualifying people",
    "Check whether someone else can claim the taxpayer and whether the taxpayer has any dependents or qualifying people.",
  ],
  "Q-010": [
    "Date of birth and blindness",
    "Record the taxpayer’s date of birth and blindness status. Age-related checks require the appropriate review.",
  ],
  "Q-011": [
    "Expected W-2 statements",
    "List the employers and wage statements expected for the year, including any missing or corrected statements.",
  ],
  "Q-012": [
    "W-2 details",
    "Each statement needs its complete wage, withholding, employer, employee and additional-box information.",
  ],
  "Q-013": [
    "Corrected or duplicate W-2s",
    "Check corrections, duplicate statements, continuation pages and employer differences.",
  ],
  "Q-015": [
    "Payroll withholding checks",
    "Compare Social Security and Medicare wages and withholding with the source statements.",
  ],
  "Q-016": [
    "Special wage items",
    "Check tips, overtime, employee benefits, sick pay and other special wage items.",
  ],
  "Q-017": [
    "Other income",
    "Ask about all other income, including income that did not come with a tax form.",
  ],
  "Q-018": [
    "Employer benefits",
    "Compare benefits reported by the taxpayer with the W-2s and supporting statements.",
  ],
  "Q-019": [
    "Adjustments to income",
    "Ask about retirement contributions, health savings, student-loan interest and other adjustments.",
  ],
  "Q-020": [
    "Additional deductions",
    "Ask about tips, overtime, vehicle-loan interest and age-related deduction circumstances.",
  ],
  "Q-022": [
    "Tax credits",
    "Ask about health coverage, education, adoption, energy improvements, vehicles and other credit circumstances.",
  ],
  "Q-023": [
    "Other taxes",
    "Check for tax obligations beyond regular income tax, such as household employment or special retirement-account taxes.",
  ],
  "Q-024": [
    "Other payments",
    "Ask about estimated payments, prior-year amounts and payments other than W-2 withholding.",
  ],
  "Q-025": [
    "Prior-year amounts and repayments",
    "Check carryovers from prior years and circumstances that may require repayment of an earlier tax benefit.",
  ],
  "Q-026": [
    "Elections and special treatment",
    "Check tax elections, spouse-related matters and other special return circumstances.",
  ],
  "Q-027": [
    "International and territory matters",
    "Ask about foreign or territory income, residence, accounts and other international circumstances.",
  ],
  "Q-028": [
    "Additional documents",
    "Identify other records and whether they affect the federal return. Supporting documents alone do not determine eligibility.",
  ],
};
const questionSections: Record<string, string> = {
  "SCR-RETURN": "Return basics",
  "SCR-PERSON": "Taxpayer and household",
  "SCR-W2": "Wages and W-2 statements",
  "SCR-INCOME": "Other income and benefits",
  "SCR-DEDUCTION": "Deductions",
  "SCR-CREDIT": "Credits",
  "SCR-TAX": "Other taxes",
  "SCR-PAYMENT": "Payments",
  "SCR-SPECIAL": "Additional circumstances",
};
const fieldLabels: Record<string, string> = {
  "return.accounting_period": "Accounting period",
  "return.bankruptcy_case": "Bankruptcy circumstances",
  "return.decedent_case": "Deceased taxpayer",
  "return.kind": "Return type",
  "return.preparation_date": "Preparation date",
  "return.source_cutoff_at": "Information collected through",
  "return.special_relief_case": "Special relief circumstances",
  "return.tax_year": "Tax year",
  "workflow.requested_service": "Requested service",
  "taxpayer.blind": "Is the taxpayer blind?",
  "taxpayer.citizenship": "Citizenship",
  "taxpayer.claimable_status": "Can someone else claim the taxpayer?",
  "taxpayer.date_of_birth": "Date of birth",
  "taxpayer.dependent_count": "Number of dependents",
  "taxpayer.filing_status": "Filing status",
  "taxpayer.marital_status_year_end": "Marital status at year end",
  "taxpayer.qualifying_person_count": "Number of qualifying people",
  "taxpayer.residence_all_year": "Full-year residence",
  "taxpayer.residence_jurisdiction": "Place of residence",
  "w2.expected_count": "Number of expected W-2 statements",
  "INT.taxpayer.name": "Taxpayer name",
  "INT.taxpayer.mailing_address": "Mailing address",
  "INT.taxpayer.contact": "Contact details",
  "INT.taxpayer.occupation": "Occupation",
  "INT.return.designations": "Return preferences",
  "INT.taxpayer.ip_pin": "Identity protection PIN",
  "INT.refund.intent": "Refund preferences",
  "INT.return.preparer_profile": "Preparer details",
  "INT.inventory.expected_statement": "Expected wage statement",
};
function plainFieldTitle(record: Row): string {
  return (
    fieldLabels[record.id] ||
    friendlyFieldLabel(
      String(record.name || "")
        .replace(/^INT[. ]/i, "")
        .replace(/[._]/g, " "),
    ) ||
    "Additional information"
  );
}
function fieldSection(record: Row): string {
  if (
    /^INT\.taxpayer\.(name|mailing_address|contact|occupation)$/.test(record.id)
  )
    return "Taxpayer details";
  if (/^taxpayer\./.test(record.id)) return "Taxpayer and household";
  if (/^w2\./.test(record.id)) return "Wages and W-2 statements";
  if (/^INT\./.test(record.id)) return "Return preferences";
  return "Return basics";
}

/** This is presentation filtering only. Every write rechecks the existing capture guard. */
export function buildIntakeCards(
  catalog: Row,
  kind: string,
): { cards: IntakeCard[]; followUpCount: number } {
  if (kind === "questionnaire") {
    const questions: Row[] = Array.isArray(catalog.questions)
      ? catalog.questions
      : [];
    const cards = questions
      .filter((record) => canCapture(record, kind, "preparer"))
      .map((record) => {
        const schema = schemaForQuestion(record, catalog.choiceMasters || []);
        const [title, help] = questionCopy[record.id] || [
          "Additional question",
          "Record the taxpayer’s response when guided capture is available.",
        ];
        return {
          record,
          captureKind: kind,
          title,
          help,
          section:
            questionSections[record.screenId] || "Additional circumstances",
          schema,
          available:
            !schema["x-intake-unavailable"] && !hasTechnicalDependency(schema),
        };
      });
    return { cards, followUpCount: questions.length - cards.length };
  }
  const facts: Row[] = kind === "supplemental-facts" ? [] : catalog.facts || [];
  const supplemental: Row[] = (catalog.supplementalFacts || []).filter(
    (record: Row) =>
      kind === "supplemental-facts" ||
      [
        "INT.taxpayer.name",
        "INT.taxpayer.mailing_address",
        "INT.taxpayer.contact",
      ].includes(record.id),
  );
  const candidates = [
    ...facts.map((record) => ({ record, captureKind: "facts" })),
    ...supplemental.map((record) => ({
      record,
      captureKind: "supplemental-facts",
    })),
  ];
  const cards: IntakeCard[] = [];
  let followUpCount = 0;
  for (const { record, captureKind } of candidates) {
    const schema = record.valueSchema || record.detail?.value_schema;
    if (
      !canCapture(record, captureKind, "preparer") ||
      !schema ||
      schema["x-intake-unavailable"] ||
      hasTechnicalDependency(schema)
    ) {
      followUpCount++;
      continue;
    }
    cards.push({
      record,
      captureKind,
      schema,
      available: true,
      title: plainFieldTitle(record),
      section: fieldSection(record),
      help: /^(INT\.)?taxpayer\./.test(record.id)
        ? "Enter the information provided for this taxpayer. Use synthetic details only."
        : "Record the information explicitly. Leave it unanswered if it has not been provided.",
    });
  }
  return { cards, followUpCount };
}

/** Display order only: original records, source IDs, typed values and capture kinds are retained. */
export function groupIntakeCards(
  cards: IntakeCard[],
  kind: string,
): [string, IntakeCard[]][] {
  const sections = new Map<string, IntakeCard[]>();
  for (const card of cards)
    sections.set(card.section, [...(sections.get(card.section) || []), card]);
  const result = [...sections.entries()];
  if (kind === "questionnaire") return result;
  const order = [
    "Taxpayer details",
    "Taxpayer and household",
    "Return basics",
    "Wages and W-2 statements",
    "Return preferences",
  ];
  const rank = (section: string) =>
    order.includes(section) ? order.indexOf(section) : order.length;
  return result.sort(([left], [right]) => rank(left) - rank(right));
}
export function intakeSteps(
  cards: IntakeCard[],
  kind: string,
): [string, IntakeCard[]][] {
  return kind === "questionnaire"
    ? cards.map((card) => [card.section, [card]])
    : groupIntakeCards(cards, kind);
}

/** Never render opaque metadata, reference IDs or arbitrary legacy objects as an answer. */
export function intakeAnswerSummary(value: unknown, schema: Row): string {
  if (value === undefined) return "Not answered";
  if (schema["x-intake-unavailable"] || hasTechnicalDependency(schema))
    return "Draft response recorded";
  const unsafe = (item: unknown, depth = 0): boolean => {
    if (depth > 12) return true;
    if (Array.isArray(item)) return item.some((v) => unsafe(v, depth + 1));
    if (item && typeof item === "object")
      return Object.entries(item).some(
        ([key, v]) =>
          /(?:^|_)(?:id|ids|sha256|hash|artifact|schema|contract|version|signature)(?:_|$)/i.test(
            key,
          ) || unsafe(v, depth + 1),
      );
    return (
      typeof item === "string" &&
      (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(item) ||
        /^[0-9a-f]{64}$/i.test(item) ||
        /^\s*[\[{]/.test(item))
    );
  };
  if (unsafe(value)) return "Draft response recorded";
  const unwrap =
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    (value as Row).state === "KNOWN"
      ? (value as Row).value
      : value;
  if (
    unwrap &&
    typeof unwrap === "object" &&
    !Array.isArray(unwrap) &&
    (unwrap as Row).items &&
    typeof (unwrap as Row).items === "object" &&
    !Array.isArray((unwrap as Row).items)
  ) {
    return `${Object.keys((unwrap as Row).items).length} screening answers recorded`;
  }
  const summary = friendlyValue(value);
  return summary.length > 180 ? `${summary.slice(0, 177)}…` : summary;
}
export function intakeAnswerState(
  value: unknown,
): "unanswered" | "needs-attention" | "recorded" {
  if (value === undefined) return "unanswered";
  const needsAttention = (item: unknown, depth = 0): boolean => {
    if (depth > 12) return true;
    if (typeof item === "string")
      return [
        "UNKNOWN",
        "CONFLICT",
        "UNDECIDED",
        "UNRESOLVED",
        "PENDING_POSTCALC",
      ].includes(item);
    if (Array.isArray(item))
      return item.some((v) => needsAttention(v, depth + 1));
    if (item && typeof item === "object")
      return Object.values(item).some((v) => needsAttention(v, depth + 1));
    return false;
  };
  return needsAttention(value) ? "needs-attention" : "recorded";
}
export function intakeContextMatches(
  current: { selected: Row | null; kind: string },
  returnId: string,
  kind: string,
  revision?: number,
): boolean {
  return (
    current.selected?.id === returnId &&
    current.kind === kind &&
    (revision === undefined || current.selected?.revision === revision)
  );
}
function ownValue(selected: Row, container: string, id: string): unknown {
  const values = selected[container];
  return values && Object.prototype.hasOwnProperty.call(values, id)
    ? values[id]
    : undefined;
}
function cloneValue(value: unknown): unknown {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}
function valueKey(value: unknown): string {
  const ordered = (v: unknown): unknown =>
    Array.isArray(v)
      ? v.map(ordered)
      : v && typeof v === "object"
        ? Object.fromEntries(
            Object.entries(v)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([k, x]) => [k, ordered(x)]),
          )
        : v;
  return JSON.stringify(ordered(value));
}
export function intakeRequest(
  selected: Row,
  card: IntakeCard,
  role: string,
  value: unknown,
): IntakeRequest {
  if (!canCapture(card.record, card.captureKind, role))
    throw Object.assign(new Error("Your role cannot edit this information."), {
      code: "FORBIDDEN",
    });
  if (
    typeof selected?.id !== "string" ||
    !selected.id ||
    !Number.isSafeInteger(selected.revision) ||
    selected.revision < 1
  )
    throw Object.assign(new Error("Refresh this return before saving."), {
      code: "REVISION_REQUIRED",
    });
  if (
    !card.available ||
    card.schema["x-intake-unavailable"] ||
    hasTechnicalDependency(card.schema)
  )
    throw Object.assign(
      new Error("Guided capture is not available for this item."),
      { code: "INTAKE_UNAVAILABLE" },
    );
  const pairedSchema =
    card.schema["x-intake-stateful"]?.valueSchema ?? card.schema;
  const captureValue =
    pairedSchema.properties?.date_of_birth &&
    pairedSchema.properties?.age_for_2025
      ? normalizeTaxAge(value)
      : value;
  const validation = validateIntakeValue(captureValue, card.schema);
  if (!validation.valid)
    throw Object.assign(new Error(validation.errors.join(" ")), {
      code: "INTAKE_VALUE_INVALID",
      validationErrors: validation.errors,
    });
  const invalidFields = validateTaxFields(card.schema, captureValue, {
    rootId: "legacy-save",
    rootLabel: card.title,
    sectionId: "legacy-intake",
    sectionLabel: card.section,
    presentation: "legacy",
  }).filter(
    (issue) =>
      issue.code !== "ANSWER_UNKNOWN" && issue.code !== "ANSWER_CONFLICT",
  );
  if (invalidFields.length) {
    const messages = invalidFields.map(
      (issue) => `${issue.sectionLabel} — ${issue.fieldLabel}: ${issue.reason}`,
    );
    throw Object.assign(new Error(messages.join(" ")), {
      code: "INTAKE_VALUE_INVALID",
      validationErrors: messages,
    });
  }
  const container = card.captureKind === "questionnaire" ? "answers" : "facts";
  return {
    caseId: selected.id,
    fieldId: card.record.id,
    container,
    url: `/cases/${encodeURIComponent(selected.id)}`,
    body: {
      expectedRevision: selected.revision,
      [container]: { [card.record.id]: cloneValue(captureValue) },
    },
  };
}
export async function saveIntakeDraft(
  request: IntakeRequest,
  submit: (url: string, body: unknown) => Promise<Row> = patch,
): Promise<Row> {
  const response = await submit(request.url, request.body);
  if (
    !response ||
    response.id !== request.caseId ||
    !Number.isSafeInteger(response.revision) ||
    response.revision <= request.body.expectedRevision ||
    !Object.prototype.hasOwnProperty.call(
      response[request.container] || {},
      request.fieldId,
    ) ||
    valueKey(response[request.container][request.fieldId]) !==
      valueKey(request.body[request.container][request.fieldId])
  ) {
    throw Object.assign(
      new Error(
        "The save could not be confirmed. Refresh this return before trying again; the change may already have been saved.",
      ),
      { code: "INTAKE_CONFIRMATION_MISSING" },
    );
  }
  return response;
}
export function intakeErrorMessage(error: unknown): string {
  const code = (error as Row)?.code;
  if (code === "REVISION_CONFLICT")
    return "This return changed while you were editing. Your entry has not overwritten the newer information. Reload the latest answer before saving again.";
  if (code === "FORBIDDEN")
    return "Your current role does not allow this change. Your entry has not been saved.";
  if (code === "INTAKE_VALUE_INVALID")
    return (
      (error as Row).validationErrors || ["Check the highlighted information."]
    ).join(" ");
  if (code === "INTAKE_UNAVAILABLE")
    return "Guided capture is not available for this item. No change has been saved.";
  if (code === "REVISION_REQUIRED")
    return "Refresh the selected return before saving. Its latest revision is required.";
  if (code === "AUTHENTICATION_REQUIRED" || code === "CSRF_INVALID")
    return "Your session needs to be refreshed. Sign in again before saving.";
  return "The save could not be confirmed. Your entry is still shown. Refresh the return before trying again; the change may already have been saved.";
}

interface Editor {
  card: IntakeCard;
  returnId: string;
  revision: number;
  original: unknown;
  legacyIncompatible: boolean;
}
export function IntakeExperience({
  kind,
  catalog,
  selected,
  role,
  refresh,
  notify,
  onTechnicalDetails,
}: IntakeExperienceProps) {
  const { cards, followUpCount } = useMemo(
    () => buildIntakeCards(catalog, kind),
    [catalog, kind],
  );
  const [editing, setEditing] = useState<Editor | null>(null),
    [value, setValue] = useState<unknown>(undefined);
  const [error, setError] = useState(""),
    [blockedRetry, setBlockedRetry] = useState(false),
    [busy, setBusy] = useState(false);
  const [replacementConfirmed, setReplacementConfirmed] = useState(false);
  const [stepIndex, setStepIndex] = useState(0),
    [pendingStep, setPendingStep] = useState<number | null>(null);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const pending = useRef(false),
    latest = useRef({ selected, kind, role });
  const editorRootId = useId();
  latest.current = { selected, kind, role };
  useEffect(() => {
    setEditing(null);
    setValue(undefined);
    setError("");
    setBlockedRetry(false);
    setSavedIds(new Set());
    setStepIndex(0);
    setPendingStep(null);
  }, [selected?.id, kind]);
  const dirty = Boolean(
    editing && valueKey(value) !== valueKey(editing.original),
  );
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("zt:intake-dirty", { detail: { dirty } }),
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent("zt:intake-dirty", { detail: { dirty: false } }),
      );
    };
  }, [dirty]);
  const writable = ["workspace_admin", "preparer"].includes(role);
  const container = kind === "questionnaire" ? "answers" : "facts";
  const groups = useMemo(() => groupIntakeCards(cards, kind), [cards, kind]);
  const steps = useMemo(() => intakeSteps(cards, kind), [cards, kind]);
  const guided = kind === "questionnaire",
    currentStep = Math.min(stepIndex, Math.max(0, steps.length - 1));
  const visibleGroups = steps[currentStep] ? [steps[currentStep]] : [];

  function open(card: IntakeCard) {
    if (
      !selected ||
      pending.current ||
      !card.available ||
      !canCapture(card.record, card.captureKind, role)
    )
      return;
    const current = ownValue(
      selected,
      card.captureKind === "questionnaire" ? "answers" : "facts",
      card.record.id,
    );
    setEditing({
      card,
      returnId: selected.id,
      revision: selected.revision,
      original: cloneValue(current),
      legacyIncompatible:
        current !== undefined &&
        !validateIntakeValue(current, card.schema).valid,
    });
    setValue(cloneValue(current));
    setError("");
    setBlockedRetry(false);
    setReplacementConfirmed(false);
  }
  function cancel() {
    if (!pending.current) {
      setEditing(null);
      setValue(undefined);
      setError("");
      setBlockedRetry(false);
      setPendingStep(null);
    }
  }
  function changeStep(index: number) {
    if (
      pending.current ||
      index < 0 ||
      index >= steps.length ||
      index === currentStep
    )
      return;
    if (dirty) {
      setPendingStep(index);
      return;
    }
    cancel();
    setStepIndex(index);
    setPendingStep(null);
  }
  function discardAndMove() {
    if (pending.current || pendingStep === null) return;
    cancel();
    setStepIndex(pendingStep);
    setPendingStep(null);
  }
  async function save() {
    if (
      pending.current ||
      !editing ||
      !selected ||
      blockedRetry ||
      (editing.legacyIncompatible && !replacementConfirmed)
    )
      return;
    const attempt = editing,
      current = latest.current;
    if (!intakeContextMatches(current, attempt.returnId, kind)) return;
    if (current.selected?.revision !== attempt.revision) {
      setError(intakeErrorMessage({ code: "REVISION_CONFLICT" }));
      setBlockedRetry(true);
      return;
    }
    let request: IntakeRequest;
    try {
      request = intakeRequest(
        { ...current.selected, revision: attempt.revision },
        attempt.card,
        current.role,
        value,
      );
    } catch (reason) {
      if ((reason as Row)?.code === "INTAKE_VALUE_INVALID") {
        const issues = validateTaxFields(attempt.card.schema, value, {
          rootId: editorRootId,
          rootLabel: attempt.card.title,
          sectionId: editorRootId,
          sectionLabel: attempt.card.section,
          presentation: "legacy",
        });
        if (issues[0]) {
          focusValidationIssue(issues[0]);
          setError(
            "Review the highlighted fields in this section. Each field explains what is needed.",
          );
          return;
        }
      }
      setError(intakeErrorMessage(reason));
      return;
    }
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      await saveIntakeDraft(request);
      const sameReturn = intakeContextMatches(
        latest.current,
        attempt.returnId,
        kind,
      );
      if (sameReturn) {
        setEditing(null);
        setValue(undefined);
        setPendingStep(null);
        setSavedIds((ids) => new Set(ids).add(attempt.card.record.id));
      }
      notify(
        sameReturn
          ? "Draft answer saved. It still needs validation and review."
          : "The draft answer was saved to the previously selected return.",
      );
      try {
        await refresh();
      } catch {
        if (intakeContextMatches(latest.current, attempt.returnId, kind))
          setError(
            "The answer was saved, but the latest return could not be loaded. Refresh before editing again.",
          );
      }
    } catch (reason) {
      if (intakeContextMatches(latest.current, attempt.returnId, kind)) {
        setError(intakeErrorMessage(reason));
        setBlockedRetry(true);
      } else
        notify(
          "A save for the previously selected return could not be confirmed. Reopen that return before retrying.",
        );
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function reload() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    try {
      await refresh();
      setEditing(null);
      setValue(undefined);
      setError("");
      setBlockedRetry(false);
    } catch {
      setError(
        "The latest return could not be loaded. Your unsaved entry is still here. Try refreshing again.",
      );
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  if (!selected)
    return (
      <Empty
        title="Select a return to begin intake"
        description="Choose an existing return above. Your answers will be saved only to that selected return."
      />
    );
  const answered = cards.filter(
    (card) => ownValue(selected, container, card.record.id) !== undefined,
  ).length;
  const needsAttention = cards.filter(
    (card) =>
      intakeAnswerState(ownValue(selected, container, card.record.id)) ===
      "needs-attention",
  ).length;
  const sectionTitle =
    kind === "questionnaire"
      ? "Taxpayer interview"
      : kind === "supplemental-facts"
        ? "Additional taxpayer details"
        : "Taxpayer information";
  return (
    <div className="intake-experience">
      <div className="intake-overview">
        <div>
          <p className="eyebrow">Return intake</p>
          <h2>{sectionTitle}</h2>
          <p>
            Work through each section. Nothing is saved until you choose Save
            answer.
          </p>
        </div>
        <div className="intake-progress">
          <strong>
            {answered} <span>of {cards.length}</span>
          </strong>
          <span>draft answers recorded</span>
          {needsAttention > 0 && (
            <span>{needsAttention} need clarification</span>
          )}
        </div>
      </div>
      <Callout title={writable ? "Draft information only" : "Read-only intake"}>
        {writable
          ? "Use synthetic taxpayer information only. Answers are saved as drafts; they do not confirm eligibility, complete a review or prepare a return."
          : "You can read the recorded information. A preparer or workspace administrator must make changes."}
      </Callout>
      {steps.length > 0 && (
        <div className="intake-wizard-header">
          <div>
            <span className="intake-question-position">
              {guided ? "Question" : "Section"} {currentStep + 1} of{" "}
              {steps.length}
            </span>
            <strong>{steps[currentStep][0]}</strong>
            <progress
              max={steps.length}
              value={currentStep + 1}
              aria-label={
                guided
                  ? "Question position in the interview"
                  : "Section position in personal information"
              }
            />
          </div>
          <label>
            {guided ? "Jump to a question" : "Jump to a section"}
            <select
              value={currentStep}
              disabled={busy}
              onChange={(event) => changeStep(Number(event.target.value))}
            >
              {guided
                ? groups.map(([section, entries]) => (
                    <optgroup label={section} key={section}>
                      {entries.map((card) => {
                        const index = cards.indexOf(card);
                        const status = intakeAnswerState(
                          ownValue(selected, "answers", card.record.id),
                        );
                        return (
                          <option key={card.record.id} value={index}>
                            {index + 1}. {card.title} —{" "}
                            {status === "unanswered"
                              ? "Not answered"
                              : status === "needs-attention"
                                ? "Needs clarification"
                                : "Draft recorded"}
                          </option>
                        );
                      })}
                    </optgroup>
                  ))
                : steps.map(([section, entries], index) => (
                    <option key={section} value={index}>
                      {index + 1}. {section} —{" "}
                      {
                        entries.filter(
                          (card) =>
                            ownValue(selected, "facts", card.record.id) !==
                            undefined,
                        ).length
                      }{" "}
                      of {entries.length} draft answers
                    </option>
                  ))}
            </select>
          </label>
        </div>
      )}
      {pendingStep !== null && (
        <div className="intake-discard-prompt" role="alert">
          <strong>Keep your unsaved answer?</strong>
          <p>
            Moving to another {guided ? "question" : "section"} will discard
            this unsaved entry. The previously saved answer will remain
            unchanged.
          </p>
          <div className="intake-editor-actions">
            <Button disabled={busy} onClick={() => setPendingStep(null)}>
              Keep editing
            </Button>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={discardAndMove}
            >
              Discard unsaved entry and continue
            </Button>
          </div>
        </div>
      )}
      {error && !editing && (
        <div className="intake-error" role="alert">
          <p>{error}</p>
          <Button disabled={busy} onClick={() => void reload()}>
            <RefreshCw size={14} />
            Refresh return
          </Button>
        </div>
      )}
      {busy && !editing && (
        <p className="intake-pending" role="status">
          Finishing the pending request. Please wait before editing another
          answer.
        </p>
      )}
      {groups.length === 0 && (
        <Empty
          title="No guided fields available"
          description="There are no directly editable fields for this section. Related information may need document processing or a later review."
        />
      )}
      {visibleGroups.map(([section, entries]) => (
        <section
          className={`intake-section${guided ? " intake-guided" : ""}`}
          key={section}
          aria-label={section}
        >
          {!guided && (
            <div className="intake-section-heading">
              <h3>{section}</h3>
              <span>
                {entries.length} {entries.length === 1 ? "item" : "items"}
              </span>
            </div>
          )}
          <div className="intake-card-grid">
            {entries.map((card) => {
              const stored = ownValue(
                selected,
                card.captureKind === "questionnaire" ? "answers" : "facts",
                card.record.id,
              );
              const isEditing =
                editing?.card.record.id === card.record.id &&
                editing?.returnId === selected.id;
              const allowed =
                card.available &&
                canCapture(card.record, card.captureKind, role);
              const answeredCard = stored !== undefined;
              const uncertain = intakeAnswerState(stored) === "needs-attention";
              return (
                <article
                  className={`intake-card${isEditing ? " editing" : ""}`}
                  key={card.record.id}
                >
                  <div className="intake-card-heading">
                    <h4>{card.title}</h4>
                    <span
                      className={`intake-answer-state${uncertain ? " uncertain" : answeredCard ? " recorded" : ""}`}
                    >
                      {uncertain ? (
                        "Needs clarification"
                      ) : answeredCard ? (
                        <>
                          <Check size={12} />
                          Draft recorded
                        </>
                      ) : (
                        "Not answered"
                      )}
                    </span>
                  </div>
                  <p className="intake-card-help">{card.help}</p>
                  {isEditing ? (
                    <div className="intake-editor">
                      {editing?.legacyIncompatible && !replacementConfirmed ? (
                        <div className="intake-legacy">
                          <strong>Keep or replace the existing draft?</strong>
                          <p>
                            The saved answer uses an older or unsupported
                            format. It has not been changed. Starting a
                            replacement opens blank fields; only Save answer
                            replaces the stored draft.
                          </p>
                          <Button
                            disabled={busy}
                            onClick={() => {
                              setReplacementConfirmed(true);
                              setValue(undefined);
                            }}
                          >
                            Start replacement answer
                          </Button>
                        </div>
                      ) : (
                        <IntakeValueFields
                          schema={card.schema}
                          value={value}
                          onChange={(next) => {
                            setValue(next);
                            if (!blockedRetry) setError("");
                          }}
                          label={card.title}
                          rootId={editorRootId}
                          sectionLabel={card.section}
                          disabled={busy || !allowed}
                        />
                      )}
                      {error && (
                        <div className="intake-error" role="alert">
                          <p>{error}</p>
                          {blockedRetry && (
                            <Button
                              disabled={busy}
                              onClick={() => void reload()}
                            >
                              <RefreshCw size={14} />
                              Reload latest answer
                            </Button>
                          )}
                        </div>
                      )}
                      <div className="intake-editor-actions">
                        <Button
                          disabled={
                            busy ||
                            blockedRetry ||
                            !allowed ||
                            (Boolean(editing?.legacyIncompatible) &&
                              !replacementConfirmed)
                          }
                          onClick={() => void save()}
                        >
                          {busy ? "Saving…" : "Save answer"}
                        </Button>
                        <Button
                          variant="ghost"
                          disabled={busy}
                          onClick={cancel}
                        >
                          Cancel
                        </Button>
                      </div>
                      <p className="intake-save-help">
                        {blockedRetry
                          ? "Reloading discards this unsaved entry and opens the current return."
                          : "Cancel leaves the saved answer unchanged."}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="intake-answer-preview">
                        <span>{answeredCard ? "Saved draft" : "Answer"}</span>
                        <p>{intakeAnswerSummary(stored, card.schema)}</p>
                      </div>
                      {!card.available && (
                        <p className="intake-unavailable">
                          <CircleHelp size={14} />
                          <span>
                            Guided capture for this item is not available yet.
                            It needs a connected document or review workflow.
                          </span>
                        </p>
                      )}
                      <div className="intake-card-actions">
                        {allowed && (
                          <Button
                            variant={answeredCard ? "secondary" : ""}
                            disabled={busy || Boolean(editing)}
                            onClick={() => open(card)}
                          >
                            <Pencil size={13} />
                            {answeredCard ? "Edit answer" : "Answer"}
                          </Button>
                        )}
                        {!writable && (
                          <span className="intake-readonly">Read only</span>
                        )}
                        {onTechnicalDetails && (
                          <Button
                            variant="ghost"
                            onClick={() => onTechnicalDetails(card.record)}
                          >
                            Technical Details
                          </Button>
                        )}
                        {savedIds.has(card.record.id) && (
                          <span className="intake-saved" role="status">
                            Saved as draft
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ))}
      {steps.length > 0 && (
        <div className="intake-wizard-footer">
          <Button
            variant="secondary"
            disabled={busy || currentStep === 0}
            onClick={() => changeStep(currentStep - 1)}
          >
            <ArrowLeft size={14} />
            Back
          </Button>
          <p>
            {currentStep + 1 === steps.length
              ? `You have reached the last ${guided ? "interview question" : "information section"}. Unanswered items remain open; this is not a completed review.`
              : guided
                ? "Next moves to the following question. It does not save or answer this one."
                : "Next moves to the following section. It does not save any entries."}
          </p>
          <Button
            disabled={busy || currentStep + 1 === steps.length}
            onClick={() => changeStep(currentStep + 1)}
          >
            Next
            <ArrowRight size={14} />
          </Button>
        </div>
      )}
      {followUpCount > 0 && (
        <details className="intake-followups">
          <summary>
            <ChevronDown size={16} />
            <strong>
              {followUpCount} follow-up{" "}
              {followUpCount === 1 ? "check" : "checks"} happen later
            </strong>
          </summary>
          <p>
            {kind === "questionnaire"
              ? "Wage totals, deduction comparison, tax calculations, payments, refund instructions and final review depend on documents or later processing. They are not questions to fill in here and have not been marked complete."
              : "Some information comes from document processing, calculations or independent review. Those fields are not taxpayer-entry fields and cannot be set here."}
          </p>
        </details>
      )}
    </div>
  );
}
